import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate, parseLocalDate } from "@/lib/date-utils"
import { employeePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface LogEntry {
  id: number
  log_date: string | null
  employee_name: string | null
  employee_id: number
  content: string
  hidden: boolean
  added_by: string | null
  date_added: string | null
}

interface Summary {
  total_entries: number
  hidden_entries: number
}

interface EmployeeOption {
  value: number
  label: string
}

interface SelectedEmployee {
  id: number
  name: string
}

interface Props {
  logs: LogEntry[]
  start_date: string
  end_date: string
  employee_id: string
  employees: EmployeeOption[]
  selected_employee: SelectedEmployee | null
  summary: Summary
  is_admin: boolean
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }
  const s = parseLocalDate(start).toLocaleDateString("en-US", opts)
  const e = parseLocalDate(end).toLocaleDateString("en-US", opts)
  return `${s} - ${e}`
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 15

const columns: HpiColumn<LogEntry>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{formatDate(row.log_date)}</div>,
  },
  {
    header: "Employee",
    width: "max-w-[180px]",
    render: (row) => (
      <div className="flex items-center gap-1.5 text-sm">
        <a
          href={employeePath(row.employee_id)}
          className="text-hpi-active-blue hover:underline"
        >
          {row.employee_name ?? "-"}
        </a>
        {row.hidden && (
          <span className="text-red-500" title="Not visible to employee">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 640 512"
              fill="currentColor"
              className="h-3.5 w-3.5"
            >
              <path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C261.2 117 307.5 96 320 96c88.4 0 160 71.6 160 160c0 15.6-2.2 30.6-6.4 44.8l-43.2-33.9C432.6 249 432 238.6 432 228c0-26.5-21.5-48-48-48h-4c-6.7 0-12.2 5.5-12.2 12.2c0 3.1 1.2 6.1 3.3 8.3l-61-47.8zM480 256c0-88.4-71.6-160-160-160c-14.2 0-28 1.9-41.2 5.4L134.9 74C168.5 50.8 212.4 16 320 16c89.1 0 160.3 41.2 209.6 86.2c47.5 43.4 79.3 95.5 94.7 132.1c4.2 10 4.2 21.2 0 31.2c-7.7 18.5-19.8 40.7-36.2 63.7L480 256zM320 416c-79.5 0-144-64.5-144-144c0-6.9 .5-13.7 1.4-20.4L49.2 145.5C24.9 175.5 8.1 207.5 0 228.8c-4.2 10-4.2 21.2 0 31.2c15.4 36.7 47.2 88.7 94.7 132.1C143.7 434.8 214.9 476 304 476h32c0 0 0 0 0 0c35.3 0 64-28.7 64-64c0-11.7-3.1-22.6-8.6-32H320z" />
            </svg>
          </span>
        )}
      </div>
    ),
  },
  {
    header: "Log Entry",
    render: (row) => (
      <div className="text-sm whitespace-pre-wrap">{row.content}</div>
    ),
  },
  {
    header: "Added By",
    width: "max-w-[200px]",
    render: (row) => (
      <div className="text-sm">
        <div>{row.added_by ?? "-"}</div>
        <div className="text-xs text-gray-500">
          {formatDate(row.date_added)}
        </div>
      </div>
    ),
  },
]

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/employee_logs" },
  { title: "Employee Logs", href: "/reports/employee_logs" },
]

export default function EmployeeLogsReport({
  logs,
  start_date,
  end_date,
  employee_id,
  employees,
  selected_employee,
  summary,
  is_admin,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [selectedEmployee, setSelectedEmployee] = useState(employee_id)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(logs.length / PER_PAGE)
  const paginated = logs.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleUpdate = () => {
    setPage(1)
    router.get(
      "/reports/employee_logs",
      {
        start_date: startDate,
        end_date: endDate,
        employee_id: selectedEmployee,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Employee Logs Report" />

      <div className="p-6">
        {/* Legend (admin only - non-admins don't see hidden logs) */}
        {is_admin && (
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
            <span className="text-red-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 512"
                fill="currentColor"
                className="h-3.5 w-3.5"
              >
                <path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C261.2 117 307.5 96 320 96c88.4 0 160 71.6 160 160c0 15.6-2.2 30.6-6.4 44.8l-43.2-33.9C432.6 249 432 238.6 432 228c0-26.5-21.5-48-48-48h-4c-6.7 0-12.2 5.5-12.2 12.2c0 3.1 1.2 6.1 3.3 8.3l-61-47.8zM480 256c0-88.4-71.6-160-160-160c-14.2 0-28 1.9-41.2 5.4L134.9 74C168.5 50.8 212.4 16 320 16c89.1 0 160.3 41.2 209.6 86.2c47.5 43.4 79.3 95.5 94.7 132.1c4.2 10 4.2 21.2 0 31.2c-7.7 18.5-19.8 40.7-36.2 63.7L480 256zM320 416c-79.5 0-144-64.5-144-144c0-6.9 .5-13.7 1.4-20.4L49.2 145.5C24.9 175.5 8.1 207.5 0 228.8c-4.2 10-4.2 21.2 0 31.2c15.4 36.7 47.2 88.7 94.7 132.1C143.7 434.8 214.9 476 304 476h32c0 0 0 0 0 0c35.3 0 64-28.7 64-64c0-11.7-3.1-22.6-8.6-32H320z" />
              </svg>
            </span>
            Not Visible to Employee
          </div>
        )}

        {/* Date Filter Bar with Employee Select */}
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <HpiFilterSelect
            label="Employee"
            value={selectedEmployee}
            onChange={setSelectedEmployee}
            options={employees}
          />
        </HpiDateFilterBar>

        {/* Employee Header (shown when filtered to specific employee) */}
        {selected_employee && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <h2 className="text-lg font-semibold text-gray-900">
              {selected_employee.name}
            </h2>
          </div>
        )}

        {/* Data Table */}
        <HpiDataTable<LogEntry>
          title={`Employee Logs  ${formatDateRange(start_date, end_date)}`}
          columns={columns}
          data={paginated}
          keyFn={(row) => row.id}
          emptyMessage="No log entries found for the selected filters."
          pagination={
            logs.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={logs.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Summary Stats */}
        <HpiStatsBar
          className="mt-6"
          stats={[
            {
              label: "Total",
              subLabel: "Entries",
              value: summary.total_entries,
            },
            ...(is_admin
              ? [
                  {
                    label: "Hidden",
                    subLabel: "Entries",
                    value: summary.hidden_entries,
                  },
                ]
              : []),
          ]}
        />
      </div>
    </AppLayout>
  )
}
